import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  mobileTabBadgeRecipe,
  mobileTabBarListRecipe,
  mobileTabBarRecipe,
  mobileTabRecipe,
} from '../recipes/mobile.recipe';
import { Icon, type IconComponent } from './Icon';

export interface MobileTab {
  id: string;
  label: string;
  icon: IconComponent;
  /**
   * A count on the icon — calls waiting, photos still to send. Zero or
   * nothing shows no badge; over 99 reads "99+".
   */
  badge?: number;
}

export interface MobileTabBarProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onChange'> {
  /**
   * The tabs this member has, in order. Data rather than children, because
   * which tabs a member sees is decided by their permissions: the app
   * filters a list, it does not hide markup.
   */
  tabs: MobileTab[];
  /** The id of the tab whose screen is showing. */
  value: string;
  onValueChange: (id: string) => void;
}

/**
 * The phone's main navigation: a dark glass pill floating along the bottom,
 * one icon per tab, the current one lit. The tab's word is there for a
 * screen reader; the Figma draws icons alone.
 *
 *   <MobileTabBar
 *     tabs={tabsFor(member)}
 *     value={currentTab}
 *     onValueChange={(id) => navigate(pathFor(id))}
 *   />
 *
 * It sits clear of the home indicator on an iPhone and lets taps through
 * either side of the pill, so a map behind it still pans. A screen under it
 * needs room at the bottom for it; Page's mobile padding leaves that room.
 */
export const MobileTabBar = React.forwardRef<HTMLElement, MobileTabBarProps>(
  function MobileTabBar(
    {
      tabs,
      value,
      onValueChange,
      className,
      'aria-label': label = 'Main',
      ...props
    },
    ref
  ) {
    return (
      <nav
        ref={ref}
        aria-label={label}
        className={cn(mobileTabBarRecipe(), className)}
        {...props}
      >
        <ul className={mobileTabBarListRecipe()}>
          {tabs.map((tab) => {
            const active = tab.id === value;
            const count =
              tab.badge && tab.badge > 0
                ? tab.badge > 99
                  ? '99+'
                  : String(tab.badge)
                : null;
            return (
              <li key={tab.id} className="flex">
                <button
                  type="button"
                  aria-current={active ? 'page' : undefined}
                  onClick={() => onValueChange(tab.id)}
                  className={mobileTabRecipe({ active })}
                >
                  {/* Solid glyphs, as the Figma's tab bar draws them. */}
                  <Icon icon={tab.icon} weight="fill" size="lg" />
                  <span className="sr-only">{tab.label}</span>
                  {count ? (
                    <span className={mobileTabBadgeRecipe()}>
                      {count}
                      <span className="sr-only"> new</span>
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    );
  }
);
